import { useState } from 'react'
import styled from 'styled-components'
import { MatchMotivo, ScoreBreakdown, recommendationsApi } from '../../services/api'

const BreakdownContainer = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
`

const BreakdownTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FormulaDisplay = styled.div`
  background: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border: 1px dashed ${({ theme }) => theme.colors.primary}40;
`

const ComponentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ComponentCard = styled.div<{ variant: 'primary' | 'success' | 'warning' | 'danger' }>`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  border-left: 3px solid ${({ variant }) => {
        switch (variant) {
            case 'primary': return '#2196F3'
            case 'success': return '#4CAF50'
            case 'warning': return '#FF9800'
            case 'danger': return '#F44336'
            default: return '#9E9E9E'
        }
    }};
`

const ComponentLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 600;
  margin-bottom: 4px;
`

const ComponentValue = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
`

const ComponentSubtext = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
`

const ProgressBarContainer = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
`

const ProgressBar = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${({ width }) => Math.min(100, Math.max(0, width))}%;
  background: ${({ color }) => color};
  transition: width 0.3s ease;
`

const SectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin: ${({ theme }) => theme.spacing.sm} 0;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`

const PenaltyBreakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`

const PenaltyItem = styled.div`
  background: #FFF3E0;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  color: #EF6C00;
  display: flex;
  align-items: center;
  gap: 4px;
`

const FinalScoreDisplay = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primary}dd);
  color: white;
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: center;
`

const FinalScoreValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
`

const FinalScoreLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.9;
`

interface ScoreBreakdownCardProps {
    breakdown: ScoreBreakdown
    compact?: boolean
    // Optional context for the on-demand "Explicar con IA" button. If omitted,
    // the button is hidden and only the deterministic breakdown renders.
    explainContext?: {
        candidateName: string
        candidateRole?: string
        candidateYears?: number
        requestedRole: string
        opportunityName: string
        cliente?: string
        motivos: MatchMotivo[]
    }
}

const ExplainButton = styled.button`
  margin-top: ${({ theme }) => theme.spacing.md};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px dashed ${({ theme }) => theme.colors.primary}60;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primary}25;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const LLMSummaryBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  background: white;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
  /* Let long paragraphs wrap and scroll inside the box so the modal layout
     doesn't get pushed off-screen. */
  max-height: 280px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
`

export const ScoreBreakdownCard: React.FC<ScoreBreakdownCardProps> = ({ breakdown, compact = false, explainContext }) => {
    const {
        tier_value,
        tier_name,
        tier_score,
        availability_pct,
        availability_factor,
        availability_contribution,
        penalty_components,
        penalty_multiplier,
        score_after_penalty,
        bonus_components,
        final_score,
        formula_display,
    } = breakdown

    const [llmSummary, setLlmSummary] = useState<string | null>(null)
    const [llmLoading, setLlmLoading] = useState(false)
    const [llmError, setLlmError] = useState<string | null>(null)

    const handleExplain = async () => {
        if (!explainContext) return
        setLlmLoading(true)
        setLlmError(null)
        try {
            const { data } = await recommendationsApi.explain({
                candidate_name: explainContext.candidateName,
                candidate_role: explainContext.candidateRole,
                candidate_years: explainContext.candidateYears,
                requested_role: explainContext.requestedRole,
                opportunity_name: explainContext.opportunityName,
                cliente: explainContext.cliente,
                score_breakdown: breakdown,
                motivos: explainContext.motivos,
            })
            setLlmSummary(data.summary)
        } catch (err: unknown) {
            const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Error desconocido'
            setLlmError(msg)
        } finally {
            setLlmLoading(false)
        }
    }

    if (compact) {
        return (
            <BreakdownContainer>
                <FormulaDisplay>
                    📊 {formula_display}
                </FormulaDisplay>
                <ComponentsGrid>
                    <ComponentCard variant="primary">
                        <ComponentLabel>Tier {tier_value}</ComponentLabel>
                        <ComponentValue>{tier_score}</ComponentValue>
                        <ComponentSubtext>{tier_name}</ComponentSubtext>
                    </ComponentCard>
                    <ComponentCard variant="success">
                        <ComponentLabel>Disponibilidad</ComponentLabel>
                        <ComponentValue>×{(availability_factor * 100).toFixed(0)}%</ComponentValue>
                        <ProgressBarContainer>
                            <ProgressBar width={availability_pct} color="#4CAF50" />
                        </ProgressBarContainer>
                    </ComponentCard>
                    <ComponentCard variant={penalty_components.total_penalty > 0 ? 'warning' : 'success'}>
                        <ComponentLabel>Penalización</ComponentLabel>
                        <ComponentValue>
                            {penalty_components.total_penalty > 0
                                ? `-${(penalty_components.total_penalty * 100).toFixed(0)}%`
                                : '0%'}
                        </ComponentValue>
                        <ProgressBarContainer>
                            <ProgressBar width={penalty_components.total_penalty * 100} color={penalty_components.total_penalty > 0 ? '#FF9800' : '#4CAF50'} />
                        </ProgressBarContainer>
                    </ComponentCard>
                </ComponentsGrid>
            </BreakdownContainer>
        )
    }

    return (
        <BreakdownContainer>
            <BreakdownTitle>
                📊 Desglose del Score
            </BreakdownTitle>

            <FormulaDisplay>
                {formula_display}
            </FormulaDisplay>

            <ComponentsGrid>
                {/* Tier Score */}
                <ComponentCard variant="primary">
                    <ComponentLabel>🎯 Score Base (Tier {tier_value})</ComponentLabel>
                    <ComponentValue>{tier_score} pts</ComponentValue>
                    <ComponentSubtext>{tier_name}</ComponentSubtext>
                    <ProgressBarContainer>
                        <ProgressBar width={tier_score} color="#2196F3" />
                    </ProgressBarContainer>
                </ComponentCard>

                {/* Availability */}
                <ComponentCard variant="success">
                    <ComponentLabel>📅 Disponibilidad</ComponentLabel>
                    <ComponentValue>{availability_pct.toFixed(0)}%</ComponentValue>
                    <ComponentSubtext>Factor: ×{availability_factor.toFixed(2)} = {availability_contribution.toFixed(1)} pts</ComponentSubtext>
                    <ProgressBarContainer>
                        <ProgressBar width={availability_pct} color="#4CAF50" />
                    </ProgressBarContainer>
                </ComponentCard>

                {/* Penalty */}
                <ComponentCard variant={penalty_components.total_penalty > 0 ? 'warning' : 'success'}>
                    <ComponentLabel>🔄 Penalización</ComponentLabel>
                    <ComponentValue>
                        {penalty_components.total_penalty > 0
                            ? `-${(penalty_components.total_penalty * 100).toFixed(1)}%`
                            : '0%'}
                    </ComponentValue>
                    <ComponentSubtext>Factor: ×{penalty_multiplier.toFixed(2)} = {score_after_penalty.toFixed(1)} pts</ComponentSubtext>
                    <ProgressBarContainer>
                        <ProgressBar width={penalty_components.total_penalty * 100} color={penalty_components.total_penalty > 0 ? '#FF9800' : '#4CAF50'} />
                    </ProgressBarContainer>
                </ComponentCard>
            </ComponentsGrid>

            {/* Penalty Details */}
            {penalty_components.total_penalty > 0 && (
                <>
                    <SectionDivider>Detalle Penalizaciones</SectionDivider>
                    <PenaltyBreakdown>
                        {penalty_components.experience_penalty > 0 && (
                            <PenaltyItem>
                                📊 Experiencia: -{(penalty_components.experience_penalty * 100).toFixed(1)}%
                                <span style={{ opacity: 0.7 }}>(w={penalty_components.w_experience})</span>
                            </PenaltyItem>
                        )}
                        {penalty_components.role_penalty > 0 && (
                            <PenaltyItem>
                                👔 Rol: -{(penalty_components.role_penalty * 100).toFixed(1)}%
                                <span style={{ opacity: 0.7 }}>(w={penalty_components.w_role})</span>
                            </PenaltyItem>
                        )}
                        {penalty_components.project_value_penalty > 0 && (
                            <PenaltyItem>
                                💰 Importe: -{(penalty_components.project_value_penalty * 100).toFixed(1)}%
                                <span style={{ opacity: 0.7 }}>(w={penalty_components.w_project_value})</span>
                            </PenaltyItem>
                        )}
                        {(penalty_components.tenure_penalty ?? 0) > 0 && (
                            <PenaltyItem>
                                ⏳ Tenure cliente ({penalty_components.tenure_months ?? 0}m): -{((penalty_components.tenure_penalty ?? 0) * 100).toFixed(1)}%
                                <span style={{ opacity: 0.7 }}>(w={penalty_components.w_tenure_client ?? 0})</span>
                            </PenaltyItem>
                        )}
                    </PenaltyBreakdown>
                </>
            )}

            {/* Overqualification */}
            {(breakdown.overqualification_penalty ?? 0) > 0 && (
                <>
                    <SectionDivider>Sobrecualificación</SectionDivider>
                    <PenaltyBreakdown>
                        <PenaltyItem>
                            ⚠️ Perfil sobre-cualificado: -{breakdown.overqualification_penalty?.toFixed(1)} pts
                            {breakdown.role_rank_gap ? (
                                <span style={{ opacity: 0.7 }}>({breakdown.role_rank_gap} nivel{breakdown.role_rank_gap > 1 ? 'es' : ''})</span>
                            ) : null}
                        </PenaltyItem>
                    </PenaltyBreakdown>
                </>
            )}

            {/* Bonuses */}
            {bonus_components.total_bonus > 0 && (
                <>
                    <SectionDivider>Bonificaciones</SectionDivider>
                    <PenaltyBreakdown>
                        {bonus_components.sector_match_bonus > 0 && (
                            <PenaltyItem style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                                🏢 Sector: +{bonus_components.sector_match_bonus} pts
                            </PenaltyItem>
                        )}
                        {bonus_components.technology_match_bonus > 0 && (
                            <PenaltyItem style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                                💻 Tecnología: +{bonus_components.technology_match_bonus.toFixed(1)} pts
                            </PenaltyItem>
                        )}
                        {bonus_components.certification_bonus > 0 && (
                            <PenaltyItem style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                                📜 Certificaciones: +{bonus_components.certification_bonus} pts
                            </PenaltyItem>
                        )}
                        {(bonus_components.role_match_bonus ?? 0) > 0 && (
                            <PenaltyItem style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                                👔 Rol: +{bonus_components.role_match_bonus?.toFixed(1)} pts
                            </PenaltyItem>
                        )}
                        {(bonus_components.client_history_bonus ?? 0) > 0 && (
                            <PenaltyItem style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                                🤝 Histórico cliente ({bonus_components.client_history_count ?? 0}): +{bonus_components.client_history_bonus?.toFixed(1)} pts
                            </PenaltyItem>
                        )}
                    </PenaltyBreakdown>
                </>
            )}

            {/* Final Score */}
            <SectionDivider>Resultado Final</SectionDivider>
            <FinalScoreDisplay>
                <FinalScoreValue>{final_score.toFixed(1)}</FinalScoreValue>
                <FinalScoreLabel>puntos</FinalScoreLabel>
            </FinalScoreDisplay>

            {/* On-demand LLM explanation */}
            {explainContext && (
                <>
                    <ExplainButton type="button" onClick={handleExplain} disabled={llmLoading}>
                        {llmLoading ? '⏳ Generando...' : '🤖 Explicar con IA'}
                    </ExplainButton>
                    {llmSummary && <LLMSummaryBox>{llmSummary}</LLMSummaryBox>}
                    {llmError && (
                        <LLMSummaryBox style={{ borderLeftColor: '#F44336', color: '#C62828' }}>
                            No se pudo generar la explicación: {llmError}
                        </LLMSummaryBox>
                    )}
                </>
            )}
        </BreakdownContainer>
    )
}

export default ScoreBreakdownCard
